import React from "react";
import "./PiPopupWindowPreview.css";
import classNames from "classnames";

interface PiPopupWindowPreviewProps {
  title?: React.ReactNode;
  body: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * `fit`: medium width, height follows the content (capped, body scrolls).
   * `hug`: width and height both follow the content (capped at 90%), for
   * popups with a single short row such as Set Outfit.
   */
  size?: "large" | "medium" | "compact" | "small" | "full" | "fit" | "hug";
  hideBottom?: Boolean;
  /**
   * A full-width header bar (e.g. PiPopupHeaderBar) instead of `title`. The popup then
   * drops its old padding: the bar spans the width with a hairline under it, and the
   * body gets one even padding (Design Studio `screens/popup-headers/a-header-bar.html`).
   */
  header?: React.ReactNode;
}

export default function PiPopupWindowPreview(props: PiPopupWindowPreviewProps) {
  const { title, body, footer, size, hideBottom, header } = { ...props };
  const hasHeaderBar = Boolean(header) && size !== "full";

  //const sizeClasses = size ? "pipopupwindowpreview-size-" + size : "pipopupwindowpreview-size-large";

  const sizeClasses = classNames({
    "pipopupwindowpreview-size-large": size === undefined || size === "large",
    "pipopupwindowpreview-size-compact": size === "compact",
    "pipopupwindowpreview-size-small": size === "small",
    "w-[90%] h-[55%]": size === "medium",
    "w-[90%] max-h-[90%] flex flex-col": size === "fit",
    // max-content: an absolutely positioned box at left 50% would otherwise
    // shrink-wrap to the remaining half of the viewport
    "w-max max-w-[90%] max-h-[90%] flex flex-col": size === "hug",
    "w-full h-full rounded-none p-0": size === "full",
    "px-[16px] py-[32px] rounded-[5px]": size !== "full" && !hasHeaderBar,
    "flex flex-col rounded-[5px] overflow-hidden": hasHeaderBar,
  });

  if (hasHeaderBar) {
    return (
      <div className={"pipopupwindowpreview-root " + sizeClasses}>
        {header}
        <div
          aria-label="pipopupwindowpreview-body"
          className="flex flex-col flex-1 min-h-0 w-full p-6 box-border"
        >
          {body}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={
          (hideBottom
            ? "pipopupwindowpreview-root-no-bottom "
            : "pipopupwindowpreview-root ") + sizeClasses
        }
      >
        {size !== "full" && (
          <div
            aria-label="pipopupwindowpreview-title"
            className="pipopupwindowpreview-title"
          >
            {title}
          </div>
        )}
        <div
          aria-label="pipopupwindowpreview-body"
          className={classNames({
            "pipopupwindowpreview-body-normal": footer,
            "pipopupwindowpreview-body-extended":
              !footer && size !== "full" && size !== "fit" && size !== "hug",
            "w-full mt-[20px] min-h-0 flex flex-col": !footer && (size === "fit" || size === "hug"),
            "h-full w-full m-0": size === "full",
          })}
        >
          {body}
        </div>
        {footer && (
          <div
            aria-label="pipopupwindowpreview-footer"
            className="pipopupwindowpreview-footer"
          >
            {footer}
          </div>
        )}
      </div>
    </>
  );
}
